import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, Info } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import NotificationSection from '@/components/home/NotificationSection';

const logoUrl = '/lovable-uploads/336b1e85-13c4-44f7-964d-d77f5368bd02.png';

const preloadLogo = () => {
  const img = new Image();
  img.src = logoUrl;
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, userProfile, loading } = useAuth();
  const isMobile = useIsMobile();

  useEffect(() => {
    preloadLogo();
  }, []);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "로그아웃 성공",
        description: "안녕히 가세요!",
      });
      
      navigate('/');
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "로그아웃 실패",
        description: error.message || "오류가 발생했습니다.",
      });
    }
  };
  
  const navLinks = [
    { name: '홈', path: '/' },
    { name: '게시판', path: '/board' },
    { name: '한인레슨/업체', path: '/business-tutoring' },
    { name: '여행정보', path: '/travel' },
    { name: '뉴스', path: '/news' },
    ...(user?.email === 'ilwoonge@gmail.com' ? [{ name: '웹훅', path: '/webhook' }] : []),
  ];
  
  return (
    <header 
      className={cn(
        "w-full transition-all duration-300",
        isScrolled ? "bg-white" : "bg-transparent"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link 
            to="/" 
            className="flex items-center"
          >
            <img 
              src={logoUrl}
              alt="VegasKeam Logo" 
              className="h-10 md:h-11"
              loading="eager"
              decoding="async"
              style={{ 
                willChange: 'auto',
                content: `url(${logoUrl})` 
              }}
            />
          </Link>
          
          <nav className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  "text-sm font-medium transition-colors hover:text-primary relative py-2",
                  isActive(link.path) 
                    ? "text-primary after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary"
                    : "text-muted-foreground"
                )}
              >
                {link.name}
              </Link>
            ))}
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            {loading ? (
              <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse"></div>
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarImage 
                        src={userProfile?.avatar_url} 
                        alt={userProfile?.username || "프로필"} 
                      />
                      <AvatarFallback>
                        {userProfile?.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex flex-col space-y-1 p-2">
                    <p className="text-sm font-medium">{userProfile?.username || "사용자"}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer"
                    onClick={() => navigate('/profile')}
                  >
                    <User className="mr-2 h-4 w-4" />
                    <span>프로필</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="cursor-pointer text-red-600"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>로그아웃</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="outline" size="sm" className="font-medium" asChild>
                  <Link to="/login">로그인</Link>
                </Button>
                <Button size="sm" className="font-medium" asChild>
                  <Link to="/register">회원가입</Link>
                </Button>
              </>
            )}
          </div>
          
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-primary"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X size={24} className="transition-transform" />
            ) : (
              <Menu size={24} className="transition-transform" />
            )}
          </button>
        </div>
      </div>
      
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent side="right" className="w-[300px] sm:w-[350px]">
          <SheetHeader>
            <SheetTitle>메뉴</SheetTitle>
          </SheetHeader>
          <div className="py-4 flex flex-col h-[calc(100%-2rem)]">
            {loading ? (
              <div className="h-10 w-40 rounded-md bg-gray-200 animate-pulse mb-6 pb-6 border-b"></div>
            ) : user && (
              <div className="flex items-center space-x-3 mb-6 pb-6 border-b">
                <Avatar className="h-10 w-10">
                  <AvatarImage 
                    src={userProfile?.avatar_url} 
                    alt={userProfile?.username || "프로필"} 
                  />
                  <AvatarFallback>
                    {userProfile?.username?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-medium">{userProfile?.username || "사용자"}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
              </div>
            )}
            
            <nav className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <SheetClose asChild key={link.path}>
                  <Link
                    to={link.path}
                    className={cn(
                      "text-base font-medium",
                      isActive(link.path) ? "text-primary" : "text-muted-foreground"
                    )}
                  >
                    {link.name}
                  </Link>
                </SheetClose>
              ))}
            </nav>
            
            <div className="border-t pt-4 mt-2">
              {loading ? (
                <div className="space-y-3">
                  <div className="h-10 w-full rounded-md bg-gray-200 animate-pulse"></div>
                  <div className="h-10 w-full rounded-md bg-gray-200 animate-pulse"></div>
                </div>
              ) : user ? (
                <div className="space-y-3">
                  <SheetClose asChild>
                    <Link
                      to="/profile"
                      className="flex items-center text-base font-medium text-muted-foreground"
                    >
                      <User className="mr-2 h-4 w-4" />
                      프로필
                    </Link>
                  </SheetClose>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center text-base font-medium text-red-500"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    로그아웃
                  </button>
                </div>
              ) : (
                <div className="flex flex-col space-y-3">
                  <SheetClose asChild>
                    <Button variant="outline" className="w-full justify-center" asChild>
                      <Link to="/login">로그인</Link>
                    </Button>
                  </SheetClose>
                  <SheetClose asChild>
                    <Button className="w-full justify-center" asChild>
                      <Link to="/register">회원가입</Link>
                    </Button>
                  </SheetClose>
                </div>
              )}
            </div>
            
            <div className="mt-auto pt-4 border-t">
              <div className="text-sm font-medium flex items-center gap-1.5 mb-2">
                <Info size={16} className="text-primary" />
                공지사항
              </div>
              <div className="mobile-notifications">
                <NotificationSection />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default Header;
